import React from 'react';

const styles = {
  container: {
    marginBottom: '30px',
  },
  sectionHeader: {
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '8px',
    color: '#333333',
    fontFamily: 'Poppins, sans-serif',
  },
  subHeader: {
    fontSize: '10px',
    color: '#666666',
    marginBottom: '12px',
    fontFamily: 'Poppins, sans-serif',
    fontStyle: 'italic',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontFamily: 'Poppins, sans-serif',
  },
  tableHeader: {
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #e0e0e0',
  },
  headerCell: {
    fontSize: '10px',
    fontWeight: '600',
    color: '#444444',
    padding: '8px 6px',
    textAlign: 'center',
    borderRight: '1px solid #f0f0f0',
  },
  phaseHeaderCell: {
    fontSize: '10px',
    fontWeight: '600',
    color: '#444444',
    padding: '8px 6px',
    textAlign: 'left',
    borderRight: '1px solid #f0f0f0',
  },
  tableRow: {
    borderBottom: '1px solid #f0f0f0',
  },
  phaseCell: {
    fontSize: '10px',
    fontWeight: '600',
    padding: '8px 6px',
    borderRight: '1px solid #f0f0f0',
    fontFamily: 'Poppins, sans-serif',
    color: '#333333',
  },
  dataCell: {
    fontSize: '9px',
    color: '#333333',
    padding: '6px',
    textAlign: 'center',
    borderRight: '1px solid #f0f0f0',
    fontFamily: 'Poppins, sans-serif',
  },
  totalCell: {
    fontSize: '9px',
    color: '#333333',
    padding: '6px',
    textAlign: 'center',
    borderRight: 'none',
    fontWeight: '500',
    fontFamily: 'Poppins, sans-serif',
  },
  emptyMessage: {
    fontSize: '10px',
    color: '#888888',
    fontStyle: 'italic',
    fontFamily: 'Poppins, sans-serif',
    textAlign: 'center',
    padding: '15px',
  }
};

// Category colors matching the cinema map
const categoryColors = {
  skills: '#4CAF50',
  knowledge: '#2196F3',
  mindset: '#FF9800',
  instruments: '#9C27B0',
};

const CategoryDistributionTable = ({ cohortResponses, allCompetenciesData }) => {
  if (!cohortResponses?.competency_counts || !allCompetenciesData) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyMessage}>
          No data available for category distribution
        </div>
      </div>
    );
  }

  // Create competency lookup map
  const competencyMap = new Map();
  allCompetenciesData.forEach(comp => {
    competencyMap.set(comp.id, {
      category: comp.category,
      phase: comp.abilities?.phase || 'Unknown'
    });
  });

  // Process data: group by phase and category  
  const phases = ['Empathize', 'Define', 'Ideate', 'Prototype', 'Test', 'Communicate'];
  const categories = ['skills', 'knowledge', 'mindset', 'instruments'];
  
  const distributionData = {};
  
  // Initialize data structure (phases as rows)
  phases.forEach(phase => {
    distributionData[phase] = {};
    categories.forEach(category => {
      distributionData[phase][category] = 0;
    });
    distributionData[phase].total = 0;
  });

  // Process competency counts
  cohortResponses.competency_counts.forEach(item => {
    const competencyDetails = competencyMap.get(item.competency_id);
    if (competencyDetails) {
      const { category, phase } = competencyDetails;
      if (distributionData[phase] && categories.includes(category)) {
        distributionData[phase][category] += item.count;
        distributionData[phase].total += item.count;
      }
    }
  });

  // Calculate totals for percentages
  const categoryTotals = {};
  categories.forEach(category => {
    categoryTotals[category] = phases.reduce((sum, phase) => 
      sum + distributionData[phase][category], 0);
  });
  
  const grandTotal = Object.values(categoryTotals).reduce((sum, total) => sum + total, 0);

  return (
    <div style={styles.container}>
      <div style={styles.sectionHeader}>Category Distribution by Phase</div>
      <div style={styles.subHeader}>
        Shows how competency selections are distributed across categories within each phase. 
        Each percentage represents the proportion of total selections in that phase by category type.
        Selections are aggregated across all participants (non-unique, multiple participants can select the same competency).
      </div>
      
      <table style={styles.table}>
        <thead style={styles.tableHeader}>
          <tr>
            <th style={styles.phaseHeaderCell}>Phase</th>
            <th style={styles.headerCell}>Skills</th>
            <th style={styles.headerCell}>Knowledge</th>
            <th style={styles.headerCell}>Mindset</th>
            <th style={styles.headerCell}>Instruments</th>
            <th style={{...styles.headerCell, borderRight: 'none'}}>Total Selections in Phase</th>
          </tr>
        </thead>
        <tbody>
          {phases.map(phase => {
            const phaseData = distributionData[phase];
            const phaseTotal = phaseData.total;
            
            return (
              <tr key={phase} style={styles.tableRow}>
                <td style={styles.phaseCell}>
                  {phase}
                </td>
                {categories.map(category => {
                  const count = phaseData[category];
                  const percentage = phaseTotal > 0 ? ((count / phaseTotal) * 100).toFixed(1) : '0.0';
                  const categoryColor = categoryColors[category] || '#666666';
                  
                  return (
                    <td key={category} style={styles.dataCell}>
                      <span style={{ color: categoryColor, fontWeight: '500' }}>
                        {percentage}%
                      </span>
                      <br />
                      <span style={{ fontSize: '8px', color: '#888' }}>
                        ({count})
                      </span>
                    </td>
                  );
                })}
                <td style={styles.totalCell}>
                  {phaseTotal}
                </td>
              </tr>
            );
          })}
          
          {/* Summary row */}
          <tr style={{...styles.tableRow, backgroundColor: '#f8f9fa'}}>
            <td style={{...styles.phaseCell, color: '#666', fontWeight: '600'}}>
              Overall Distribution
            </td>
            {categories.map(category => {
              const categoryTotal = categoryTotals[category];
              const percentage = grandTotal > 0 ? ((categoryTotal / grandTotal) * 100).toFixed(1) : '0.0';
              const categoryColor = categoryColors[category] || '#666666';
              
              return (
                <td key={category} style={{...styles.dataCell, fontWeight: '500'}}>
                  <span style={{ color: categoryColor, fontWeight: '600' }}>
                    {percentage}%
                  </span>
                  <br />
                  <span style={{ fontSize: '8px', color: '#888' }}>
                    ({categoryTotal})
                  </span>
                </td>
              );
            })}
            <td style={{...styles.totalCell, fontWeight: '600'}}>
              {grandTotal}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default CategoryDistributionTable;
